/**
 * Path2D icon library for Canvas2D rendering of economy category icons.
 * Each function returns a Path2D scaled to fit in a 16×16 box at the given (x,y) offset.
 */

function scaledPath(d: string, x: number, y: number, scale = 16 / 24): Path2D {
  const p = new Path2D();
  const src = new Path2D(d);
  const m = new DOMMatrix().translate(x, y).scale(scale, scale);
  p.addPath(src, m);
  return p;
}

export function pathLegalIncome(x: number, y: number): Path2D {
  return scaledPath('M4 20h16v2H4zm0-2h16v-2H4zm2-4h3V8H6zm5 0h3V8h-3zm5 0h3V8h-3zM2 6l10-4 10 4v2H2z', x, y);
}

export function pathCybercrime(x: number, y: number): Path2D {
  return scaledPath('M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0L19.2 12l-4.6-4.6L16 6l6 6-6 6-1.4-1.4z', x, y);
}

export function pathSubCover(x: number, y: number): Path2D {
  return scaledPath('M12 4C9 4 6 5.5 4 8h3c1.5-1.2 3.1-2 5-2s3.5.8 5 2h3c-2-2.5-5-4-8-4zM1 12c0 1.1.3 2.2.8 3.2l2.5-1.5C4.1 13.2 4 12.6 4 12c0-1.7.8-3.2 2-4.2V7c-2.9 1.4-5 4.6-5 5zm7 0a4 4 0 108 0 4 4 0 00-8 0zm4-2a2 2 0 110 4 2 2 0 010-4zm11 2c0-.6-.1-1.2-.3-1.7l-2.5 1.5c.1.4.1.8 0 1.2l2.5 1.5c.2-1 .3-1.7.3-2.5z', x, y);
}

export function pathSalaries(x: number, y: number): Path2D {
  return scaledPath('M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z', x, y);
}

export function pathRent(x: number, y: number): Path2D {
  return scaledPath('M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z', x, y);
}

export function pathVehicles(x: number, y: number): Path2D {
  return scaledPath('M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z', x, y);
}

export function pathDrones(x: number, y: number): Path2D {
  const p = new Path2D();
  const s = 16 / 24;
  // circles at corners
  for (const [cx, cy] of [[6,6],[18,6],[6,18],[18,18]] as [number,number][]) {
    p.moveTo(x + cx*s + 2.5*s, y + cy*s);
    p.arc(x + cx*s, y + cy*s, 2.5*s, 0, Math.PI * 2);
  }
  // center
  p.moveTo(x + 12*s + 2*s, y + 12*s);
  p.arc(x + 12*s, y + 12*s, 2*s, 0, Math.PI * 2);
  return p;
}

export function pathFirewall(x: number, y: number): Path2D {
  return scaledPath('M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-1 6h2v2h-2V7zm0 4h2v6h-2v-6z', x, y);
}

export function pathSubs(x: number, y: number): Path2D {
  return scaledPath('M20 7h-4V5l-2-2h-4L8 5v2H4c-1.1 0-2 .9-2 2v5c0 .75.4 1.38 1 1.73V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-3.28c.59-.35 1-.99 1-1.72V9c0-1.1-.9-2-2-2zm-6 0h-4V5h4v2z', x, y);
}

export function pathContacts(x: number, y: number): Path2D {
  return scaledPath('M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5z', x, y);
}

// ── Pipeline node icons (12×12) ─────────────────────────────────────────

function scaledPath14(d: string, x: number, y: number): Path2D {
  return scaledPath(d, x, y, 14 / 24);
}

/** Revenue: banknote/bill icon */
export function pathRevenue(x: number, y: number): Path2D {
  return scaledPath14('M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z', x, y);
}

/** Department: briefcase/business icon */
export function pathDepartment(x: number, y: number): Path2D {
  return scaledPath14('M20 7h-4V5l-2-2h-4L8 5v2H4c-1.1 0-2 .9-2 2v5c0 .75.4 1.38 1 1.73V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-3.28c.59-.35 1-.99 1-1.72V9c0-1.1-.9-2-2-2zm-6 0h-4V5h4v2z', x, y);
}

/** Audit: shield/verify icon */
export function pathAudit(x: number, y: number): Path2D {
  return scaledPath14('M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z', x, y);
}

/** Clean Cash: coin/money icon */
export function pathCoin(x: number, y: number): Path2D {
  return scaledPath14('M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 16.09V20h-2.67v-1.93c-1.71-.36-3.16-1.46-3.27-3.4h1.96c.1 1.05.82 1.87 2.65 1.87 1.96 0 2.4-.98 2.4-1.59 0-.83-.44-1.61-2.67-2.14-2.48-.6-4.18-1.62-4.18-3.67 0-1.72 1.39-2.84 3.11-3.21V4h2.67v1.95c1.86.45 2.79 1.86 2.85 3.39H14.3c-.05-1.11-.64-1.87-2.22-1.87-1.5 0-2.4.68-2.4 1.64 0 .84.65 1.39 2.67 1.94s4.18 1.36 4.18 3.85c0 1.89-1.44 2.85-3.12 3.19z', x, y);
}

type PathBuilder = (x: number, y: number) => Path2D;

const CATEGORY_PATH_MAP: Record<string, PathBuilder> = {
  legal_income: pathLegalIncome,
  cybercrime: pathCybercrime,
  sub_cover: pathSubCover,
  salaries: pathSalaries,
  rent: pathRent,
  vehicles: pathVehicles,
  drones: pathDrones,
  firewall: pathFirewall,
  subs: pathSubs,
  contacts: pathContacts,
};

export function pathForCategory(category: string | undefined, x: number, y: number): Path2D | null {
  if (!category) return null;
  const fn = CATEGORY_PATH_MAP[category];
  return fn ? fn(x, y) : null;
}
